import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface FamilyMember {
  id: string
  username: string
  display_name: string
  age?: number
  emoji?: string
  profile_color?: string
  color_theme: string
  is_parent: boolean
  total_points: number
  available_points?: number
  weekly_points: number
  lifetime_points_earned?: number
  level: number
}

interface FamilyContextType {
  members: FamilyMember[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  getMemberByUsername: (username: string) => FamilyMember | undefined
  getChildren: () => FamilyMember[]
  getParents: () => FamilyMember[]
}

const FamilyContext = createContext<FamilyContextType | undefined>(undefined)

interface FamilyProviderProps {
  children: ReactNode
}

export function FamilyProvider({ children }: FamilyProviderProps) {
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMembers = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('auth_token')
      if (!token) {
        // Silently skip fetch if no token - user may not be logged in yet
        setLoading(false)
        return
      }

      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch family members: ${response.statusText}`)
      }

      const data = await response.json()
      setMembers(data.users || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error fetching family members'
      setError(message)
      // Only log errors if not a simple auth issue
      if (!(err instanceof Error && err.message.includes('No authentication'))) {
        console.error('FamilyContext: Error fetching family members:', err)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMembers()
  }, [])

  const getMemberByUsername = (username: string): FamilyMember | undefined => {
    return members.find(m => m.username === username)
  }

  const getChildren = (): FamilyMember[] => {
    return members.filter(m => !m.is_parent).sort((a, b) => {
      // Sort by age descending (oldest first), with null ages last
      if (a.age === undefined && b.age === undefined) return 0
      if (a.age === undefined) return 1
      if (b.age === undefined) return -1
      return b.age - a.age
    })
  }

  const getParents = (): FamilyMember[] => {
    return members.filter(m => m.is_parent)
  }

  const contextValue: FamilyContextType = {
    members,
    loading,
    error,
    refresh: fetchMembers,
    getMemberByUsername,
    getChildren,
    getParents
  }

  return (
    <FamilyContext.Provider value={contextValue}>
      {children}
    </FamilyContext.Provider>
  )
}

export function useFamily(): FamilyContextType {
  const context = useContext(FamilyContext)
  if (context === undefined) {
    throw new Error('useFamily must be used within a FamilyProvider')
  }
  return context
}
